import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import * as storageService from '../services/storageService';
import { Hospital } from '../types';
import { Lock, ChevronLeft, Building, AlertCircle, User, ArrowRight, ShieldCheck } from 'lucide-react';

const HospitalStaffLogin: React.FC = () => {
    const { handle } = useParams<{ handle: string }>();
    const navigate = useNavigate();
    const [hospital, setHospital] = useState<Hospital | null>(null);
    const [loading, setLoading] = useState(true);

    const [animating, setAnimating] = useState(false);
    const [activeTab, setActiveTab] = useState<'operativo' | 'admin'>('operativo');

    // Staff PIN
    const [staffPin, setStaffPin] = useState('');
    const [staffError, setStaffError] = useState<string | null>(null);
    const [staffLoading, setStaffLoading] = useState(false);

    useEffect(() => {
        const load = async () => {
            if (handle) {
                const found = await storageService.getHospitalByHandle(handle);
                setHospital(found || null);
            }
            setLoading(false);
        };
        load();
    }, [handle]);

    // SuperAdmin / GroupAdmin bypass: if there's already an admin session,
    // skip the PIN screen and go directly to the hospital's admin panel
    useEffect(() => {
        if (!handle || loading || !hospital) return;
        try {
            const authData = storageService.getAuthData();
            const adminCtxRaw = localStorage.getItem('hospitalstatus_admin_ctx');
            const ctx = adminCtxRaw ? JSON.parse(adminCtxRaw) : null;
            
            const userRole = ctx?.role || authData?.role;
            const bypassRoles = ['SUPER_ADMIN', 'GROUP_ADMIN', 'HOSPITAL_ADMIN'];
            
            if (userRole && bypassRoles.includes(userRole)) {
                console.log(`[StaffLogin] Admin session detected (${userRole}). Bypassing PIN → /staff/paneladmin`);
                navigate(`/staff/paneladmin/${hospital.id}` + window.location.search, { replace: true });
                return;
            }
        } catch {
            // Malformed admin ctx — let user log in normally
        }
    }, [handle, navigate, hospital, loading]);


    // --- STAFF PIN ---
    const handleStaffPinKey = (num: string) => {
        if (staffPin.length < 4) setStaffPin(prev => prev + num);
    };

    const handleStaffSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!hospital || staffPin.length < 4 || staffLoading) return;
        setStaffLoading(true);
        setStaffError(null);

        const success = await storageService.staffLogin(hospital.id, staffPin);
        if (success) {
            const auth = storageService.getAuthData();
            const role = auth?.staffRole;
            const search = window.location.search;
            if (role === 'HOSPITAL_ADMIN') {
                navigate(`/staff/paneladmin/${hospital.id}` + search);
            } else if (role === 'FINANCIERO') {
                navigate(`/staff/paneladmin/${hospital.id}` + search);
            } else if (role === 'MARKETING') {
                navigate(`/staff/paneladmin/${hospital.id}` + search);
            } else {
                navigate('/staff' + search);
            }
        } else {
            setStaffError('PIN personal incorrecto');
            setStaffPin('');
            setTimeout(() => setStaffError(null), 2500);
        }
        setStaffLoading(false);
    };

    // Auto submit when staff PIN reaches 4 digits
    useEffect(() => {
        if (staffPin.length === 4) {
            handleStaffSubmit({ preventDefault: () => { } } as React.FormEvent);
        }
    }, [staffPin]);

    if (loading) return null;

    if (!hospital) {
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 text-center">
                <div className="bg-slate-200 p-4 rounded-full mb-4">
                    <AlertCircle className="w-8 h-8 text-slate-400" />
                </div>
                <h1 className="text-2xl font-bold text-slate-800 mb-2">Hospital no encontrado</h1>
                <Link to="/" className="text-indigo-600 font-bold hover:underline">Volver al Inicio</Link>
            </div>
        );
    }

    const primaryColor = hospital.branding.primaryColor || '#5B65DC';
    const secondaryColor = hospital.branding.secondaryColor || '#122056';

    // Visor elástico para el Staff PIN (longitud variable)
    const ElasticPinViewer = ({ pin, error }: { pin: string; error: boolean | string | null }) => (
        <div className={`w-full max-w-[240px] mx-auto h-14 rounded-2xl flex items-center justify-center tracking-[0.5em] text-2xl font-black transition-all duration-300 overflow-hidden px-4 ${error
            ? 'bg-red-50/80 border-2 border-red-500 text-red-500 animate-[shake_0.4s_ease-in-out]'
            : pin.length > 0
                ? 'bg-white/90 border-2 text-slate-800 shadow-[0_0_15px_rgba(0,0,0,0.05)]'
                : 'bg-white/40 border border-slate-200/50 text-slate-400'
            }`}
            style={pin.length > 0 && !error ? { borderColor: primaryColor } : {}}
        >
            {pin.length > 0 ? (
                <div className="flex gap-2">
                    {pin.split('').map((_, i) => (
                        <div key={i} className="w-3 h-3 rounded-full animate-in zoom-in duration-100" style={{ backgroundColor: primaryColor }} />
                    ))}
                </div>
            ) : (
                <span className="opacity-50 tracking-normal text-sm font-medium">Ingresa tu PIN</span>
            )}
        </div>
    );

    const NumPad = ({ onKey, onClear, onSubmit, pin, disabled, isVariableLength = false }: {
        onKey: (n: string) => void;
        onClear: () => void;
        onSubmit: (e: any) => void;
        pin: string;
        disabled?: boolean;
        isVariableLength?: boolean;
    }) => (
        <div className="grid grid-cols-3 gap-3 md:gap-4 max-w-[280px] mx-auto">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                <button
                    key={num}
                    type="button"
                    onClick={() => onKey(num.toString())}
                    disabled={disabled}
                    className="w-full aspect-square rounded-2xl bg-white/60 hover:bg-white text-slate-800 font-black text-2xl transition-all shadow-sm hover:shadow-md border border-slate-100/50 active:scale-[0.92] disabled:opacity-40"
                    style={{ color: secondaryColor }}
                >
                    {num}
                </button>
            ))}
            <button
                type="button"
                onClick={onClear}
                disabled={disabled || pin.length === 0}
                className="w-full aspect-square rounded-2xl flex items-center justify-center text-slate-400 font-bold hover:text-red-500 hover:bg-red-50/50 transition-colors disabled:opacity-40"
            >
                C
            </button>
            <button
                type="button"
                onClick={() => onKey('0')}
                disabled={disabled}
                className="w-full aspect-square rounded-2xl bg-white/60 hover:bg-white text-slate-800 font-black text-2xl transition-all shadow-sm hover:shadow-md border border-slate-100/50 active:scale-[0.92] disabled:opacity-40"
                style={{ color: secondaryColor }}
            >
                0
            </button>
            {isVariableLength ? (
                <button
                    type="submit"
                    onClick={onSubmit}
                    disabled={pin.length < 4 || disabled}
                    className="w-full aspect-square rounded-2xl text-white flex items-center justify-center transition-all disabled:opacity-50 disabled:grayscale active:scale-[0.92] disabled:cursor-not-allowed hover:brightness-110 shadow-md"
                    style={{ backgroundColor: primaryColor }}
                >
                    {disabled ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                        <ArrowRight className="w-6 h-6" />
                    )}
                </button>
            ) : (
                <button
                    type="button"
                    onClick={() => onKey('')} // Dummy for fixed layout if needed, or backspace
                    disabled={true} // Hidden/disabled for fixed 4-digit layout
                    className="w-full aspect-square rounded-2xl flex items-center justify-center opacity-0"
                />
            )}
        </div>
    );

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col font-sans relative overflow-hidden">
            {/* Fondo decorativo (Vector abstract basado en colores del hospital) */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
                <div className="absolute -top-[20%] -left-[10%] w-[70%] h-[70%] rounded-full opacity-[0.15] blur-3xl animate-[pulse_8s_ease-in-out_infinite]" style={{ backgroundColor: primaryColor }} />
                <div className="absolute -bottom-[20%] -right-[10%] w-[60%] h-[60%] rounded-full opacity-[0.1] blur-3xl animate-[pulse_10s_ease-in-out_infinite_reverse]" style={{ backgroundColor: secondaryColor }} />
            </div>

            <header className="p-6 flex items-center justify-between relative z-10">
                <Link to={`/${handle}`} className="text-slate-500 hover:text-slate-700 transition-all flex items-center gap-2 font-bold text-sm bg-white/50 backdrop-blur-md py-2 px-4 rounded-full border border-slate-200/50 hover:shadow-sm">
                    <ChevronLeft className="w-4 h-4" />
                    <span className="hidden sm:inline">Volver a {hospital.name}</span>
                    <span className="sm:hidden">Volver</span>
                </Link>
            </header>

            <main className="flex-1 flex flex-col items-center justify-center px-6 relative z-10 -mt-8">
                <div
                    className={`w-full max-w-[380px] bg-white/70 backdrop-blur-xl rounded-[2.5rem] p-8 md:p-10 text-center relative overflow-hidden transition-all duration-500 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.05),0_0_0_1px_rgba(255,255,255,0.5)_inset] border border-white/40 ${animating ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}
                >
                    {/* Top Accent */}
                    <div className="absolute top-0 left-0 right-0 h-2" style={{ backgroundColor: primaryColor }} />

                    {/* Hospital Logo */}
                    <div className="h-14 flex items-center justify-center mx-auto mb-5">
                        {hospital.branding.logoDarkUrl && window.matchMedia('(prefers-color-scheme: dark)').matches ? (
                            <img src={hospital.branding.logoDarkUrl} className="h-12 w-auto object-contain" alt="Logo" />
                        ) : hospital.branding.logoUrl ? (
                            <img src={hospital.branding.logoUrl} className="h-12 w-auto object-contain" alt="Logo" />
                        ) : (
                            <div className="w-14 h-14 rounded-2xl flex items-center justify-center bg-slate-50">
                                <Building className="w-7 h-7" style={{ color: primaryColor }} />
                            </div>
                        )}
                    </div>

                    {/* Tabs Builder */}
                    <div className="flex bg-slate-100/80 p-1.5 rounded-2xl mb-8 shadow-inner border border-slate-200/50">
                        <button
                            type="button"
                            onClick={() => setActiveTab('operativo')}
                            className={`flex-1 py-2.5 text-xs sm:text-sm font-bold rounded-xl transition-all duration-300 ${activeTab === 'operativo' ? 'bg-white shadow-sm text-slate-800' : 'text-slate-400 hover:text-slate-600'}`}
                        >
                            Staff Operativo
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveTab('admin')}
                            className={`flex-1 py-2.5 text-xs sm:text-sm font-bold rounded-xl transition-all duration-300 ${activeTab === 'admin' ? 'bg-white shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                            style={activeTab === 'admin' ? { color: primaryColor } : {}}
                        >
                            Administrador
                        </button>
                    </div>

                    {activeTab === 'operativo' ? (
                        <div className="animate-in fade-in zoom-in-95 duration-300">
                            <div className="flex items-center justify-center gap-2 mb-1">
                                <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: `${primaryColor}15` }}>
                                    <User className="w-4 h-4" style={{ color: primaryColor }} />
                                </div>
                                <h1 className="text-xl font-bold text-slate-800">Tu PIN Personal</h1>
                            </div>
                            <p className="text-slate-400 text-sm mb-6 font-medium">{hospital.name} · Identifícate</p>

                            <form onSubmit={handleStaffSubmit} className="space-y-6">
                                <div className={`h-6 mb-2 transition-all duration-300 ${staffError ? 'opacity-100 transform-none' : 'opacity-0 translate-y-2'}`}>
                                    {staffError && (
                                        <p className="text-red-500 text-xs font-bold bg-red-50/80 inline-block px-3 py-1 rounded-full border border-red-100">
                                            {staffError}
                                        </p>
                                    )}
                                </div>
                                <div className="mb-6">
                                    <ElasticPinViewer pin={staffPin} error={staffError} />
                                </div>
                                <NumPad
                                    onKey={handleStaffPinKey}
                                    onClear={() => setStaffPin('')}
                                    onSubmit={handleStaffSubmit}
                                    pin={staffPin}
                                    disabled={staffLoading}
                                    isVariableLength={true}
                                />
                            </form>
                            <p className="text-xs text-slate-400 mt-6 font-medium">Conectando usando PIN local de Operativo</p>
                        </div>
                    ) : (
                        <div className="animate-in fade-in zoom-in-95 duration-300 py-4 flex flex-col items-center">
                            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-inner border border-slate-100" style={{ backgroundColor: `${primaryColor}10` }}>
                                <ShieldCheck className="w-8 h-8" style={{ color: primaryColor }} />
                            </div>
                            <h2 className="text-lg font-bold text-slate-800 mb-2">Acceso de Administrador</h2>
                            <p className="text-slate-500 text-xs sm:text-sm font-medium px-2 mb-8 leading-relaxed">
                                Ingresa con tu <strong className="text-slate-700">correo institucional</strong> a través de la red segura.
                            </p>
                            
                            <button
                                type="button"
                                onClick={() => window.location.href = `/staff/paneladmin/${hospital.id}`}
                                className="w-full text-white font-bold py-4 rounded-2xl shadow-lg transition-all active:scale-95 flex items-center justify-center gap-2 hover:brightness-110"
                                style={{ backgroundColor: primaryColor }}
                            >
                                Ingresar de Forma Segura <ArrowRight className="w-5 h-5" />
                            </button>
                            
                            <p className="text-[10px] text-slate-400 mt-6 font-bold uppercase tracking-widest leading-loose">
                                Autorización vía Cloudflare Zero Trust
                            </p>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default HospitalStaffLogin;
